import type { components } from "@sagot/api-client";
import { ZOHO_ACCOUNTS_HOSTS, ZOHO_CALLBACK_PATH } from "@sagot/constants";

export type ZohoStatus = components["schemas"]["ZohoStatusOut"];
export type ZohoRegion = components["schemas"]["ZohoRegionOut"];

const ACCOUNTS_HOSTS: ReadonlySet<string> = new Set(ZOHO_ACCOUNTS_HOSTS);
const LOOPBACK_HOSTS: ReadonlySet<string> = new Set(["localhost", "127.0.0.1", "[::1]"]);

export interface ZohoUrlContext {
  /** The console's API base (`/api` behind the dev proxy, `https://api.<root>` in production). */
  apiBaseUrl: string;
  /** `window.location.origin`: relative API bases and the local-development check resolve against it. */
  pageOrigin: string;
}

function parseUrl(value: string, base?: string): URL | null {
  try {
    return new URL(value, base);
  } catch {
    return null;
  }
}

const hasCredentials = (url: URL): boolean => url.username !== "" || url.password !== "";

function isZohoAccountsUrl(url: URL): boolean {
  return url.protocol === "https:" && ACCOUNTS_HOSTS.has(url.hostname);
}

/** The offline fake answers with the API's own callback; only the API origin (or loopback, in local development). */
function isApiCallbackUrl(url: URL, { apiBaseUrl, pageOrigin }: ZohoUrlContext): boolean {
  if (url.pathname !== ZOHO_CALLBACK_PATH) return false;
  if (url.protocol !== "https:" && url.protocol !== "http:") return false;
  if (url.origin === parseUrl(apiBaseUrl, pageOrigin)?.origin) return true;
  const page = parseUrl(pageOrigin);
  return page !== null && LOOPBACK_HOSTS.has(page.hostname) && LOOPBACK_HOSTS.has(url.hostname);
}

/** Never navigate to anything but a Zoho accounts host over https, or the API's own Zoho callback. */
export function isZohoAuthorizeUrl(value: string, context: ZohoUrlContext): boolean {
  const url = parseUrl(value);
  if (!url || hasCredentials(url)) return false;
  return isZohoAccountsUrl(url) || isApiCallbackUrl(url, context);
}

/** The region Zoho picks from the person's account: the default of the region select. */
export function automaticRegionCode(regions: readonly ZohoRegion[]): string {
  return regions.find((region) => region.automatic)?.code ?? "";
}

/** `datacenter` for `GET /connect`: only a deliberate, non-automatic pick (anything unknown is ignored). */
export function chosenDatacenter(selected: string, regions: readonly ZohoRegion[]): string | undefined {
  const region = regions.find((candidate) => candidate.code === selected);
  return region && !region.automatic ? region.code : undefined;
}

/** The region select is only worth showing when there is something to choose. */
export const hasRegionChoice = (regions: readonly ZohoRegion[]): boolean => regions.length > 1;

/** Counts of the newest lead sync; `null` until one ran. */
export function lastSyncCounts(status: ZohoStatus): { synced: number; failed: number } | null {
  if (status.last_sync_synced === null || status.last_sync_failed === null) return null;
  return { synced: status.last_sync_synced, failed: status.last_sync_failed };
}

/** Connected, or connected and then refused by Zoho: the account details still apply. */
export const isLinked = (status: ZohoStatus): boolean => status.state === "connected" || status.state === "needs_reconnect";
